import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { User as UserIcon, Loader2, Save, Plus, X } from "lucide-react";
import { useUser, useUsers, apiClient } from "../lib/api-client";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { handleApiError } from "../lib/error-handling";
import { Badge } from "../components/ui/Badge";
import type { UserListItem } from "../lib/types";
import { ConsentFormFields } from "../components/profile/ConsentFormFields";
import { UserList } from "../components/profile/UserList";
import { getOverallConsentStatus } from "../lib/consent-utils";
import {
  consentFormSchema,
  ConsentFormValues,
  prepareConsentDataForApi,
  mapApiUserToFormValues,
  type CreateUserBody,
  type PatchUserBody,
} from "../lib/consent-schema";

export function Profile() {
  const { data: user, isLoading: isUserLoading, isAuthenticated } = useUser();
  const { data: users, isLoading: isUsersLoading } = useUsers();
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [isAddingUser, setIsAddingUser] = useState(false);

  const primaryGroupId = useMemo(() => {
    return user?.groups?.find((g) => g.group.type === "Primary")?.group.id;
  }, [user]);

  const groupedUsers = useMemo(() => {
    if (!users) return {};
    const groups: Record<string, { name: string; users: UserListItem[] }> = {};

    users.forEach((u) => {
      u.groups?.forEach((g) => {
        if (!groups[g.group.id]) {
          groups[g.group.id] = { name: g.group.name, users: [] };
        }
        groups[g.group.id].users.push(u);
      });
    });

    return groups;
  }, [users]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ConsentFormValues>({
    resolver: zodResolver(consentFormSchema),
    values: user
      ? {
          ...mapApiUserToFormValues(user),
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email || "",
          consentFormParentCarerFirstName:
            user.consentFormParentCarerFirstName || "",
          consentFormParentCarerSurname:
            user.consentFormParentCarerSurname || "",
          consentFormParentCarerAddress:
            user.consentFormParentCarerAddress || "",
          consentFormParentCarerRelationship:
            user.consentFormParentCarerRelationship || "",
          consentFormParentCarerContactNumber:
            user.consentFormParentCarerContactNumber || "",
          consentFormEmergencyContact1Name:
            user.consentFormEmergencyContact1Name || "",
          consentFormEmergencyContact1Relationship:
            user.consentFormEmergencyContact1Relationship || "",
          consentFormEmergencyContact1Phone:
            user.consentFormEmergencyContact1Phone || "",
          consentFormEmergencyContact2Name:
            user.consentFormEmergencyContact2Name || "",
          consentFormEmergencyContact2Relationship:
            user.consentFormEmergencyContact2Relationship || "",
          consentFormEmergencyContact2Phone:
            user.consentFormEmergencyContact2Phone || "",
          consentFormImportantMedicalInformation:
            user.consentFormImportantMedicalInformation || "",
          consentFormMedicationDetails: user.consentFormMedicationDetails || "",
          consentFormInjuriesDetails: user.consentFormInjuriesDetails || "",
          consentFormDietaryRequirementsDetails:
            user.consentFormDietaryRequirementsDetails || "",
          consentFormDisabilityDetails: user.consentFormDisabilityDetails || "",
          dataProcessingConsent: !!user.dataProcessingConsent,
          emergencyTreatmentConsent: !!user.emergencyTreatmentConsent,
          photoConsent:
            user.photoConsent === null
              ? undefined
              : user.photoConsent?.toString(),
          isUnder18: !!user.consentFormParticipantDateOfBirth,
          hasMedication: !!user.consentFormMedicationDetails,
          hasInjuries: !!user.consentFormInjuriesDetails,
          hasDietaryRequirements: !!user.consentFormDietaryRequirementsDetails,
          hasDisability: !!user.consentFormDisabilityDetails,
        }
      : undefined,
  });

  const updateMutation = apiClient.useMutation("patch", "/api/user/me");

  const onSubmit = async (data: ConsentFormValues) => {
    setIsUpdating(true);
    try {
      const processedData = prepareConsentDataForApi(data);

      await updateMutation.mutateAsync({
        body: processedData,
      });

      await queryClient.invalidateQueries({
        queryKey: ["get", "/api/user/me", {}],
      });
      toast.success("Profile updated successfully!");
      reset(data);
    } catch (err) {
      const apiError = handleApiError(err);
      toast.error(apiError.message || "Failed to update profile");
    } finally {
      setIsUpdating(false);
    }
  };

  const createUserMutation = apiClient.useMutation("post", "/api/user");
  const updateUserMutation = apiClient.useMutation("patch", "/api/user/{id}");

  const {
    register: registerUser,
    handleSubmit: handleSubmitUser,
    reset: resetUser,
    formState: { errors: userErrors, isSubmitting: isUserSubmitting },
  } = useForm<ConsentFormValues>({
    resolver: zodResolver(consentFormSchema),
  });

  const onAddUser = async (data: ConsentFormValues) => {
    try {
      const processedData = prepareConsentDataForApi(data);
      await createUserMutation.mutateAsync({
        body: processedData as CreateUserBody,
      });
      await queryClient.invalidateQueries({
        queryKey: ["get", "/api/user", {}],
      });
      toast.success("User added successfully!");
      setIsAddingUser(false);
      resetUser();
    } catch (err) {
      const apiError = handleApiError(err);
      toast.error(apiError.message || "Failed to add user");
    }
  };

  const onEditUser = async (id: string, data: ConsentFormValues) => {
    try {
      const processedData = prepareConsentDataForApi(data);

      await updateUserMutation.mutateAsync({
        params: { path: { id } },
        body: processedData as PatchUserBody,
      });
      await queryClient.invalidateQueries({
        queryKey: ["get", "/api/user", {}],
      });
      toast.success("User updated successfully!");
      setEditingUserId(null);
    } catch (err) {
      const apiError = handleApiError(err);
      toast.error(apiError.message || "Failed to update user");
    }
  };

  if (isUserLoading || (isAuthenticated && isUsersLoading)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 text-center">
        <div className="bg-white shadow sm:rounded-lg p-12">
          <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Sign in to view your profile
          </h3>
          <p className="text-gray-600 mb-6">
            Please sign in to manage your account and club consents.
          </p>
          <div className="flex justify-center space-x-4">
            <Link
              to="/login"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              Sign In
            </Link>
            <Link
              to="/events"
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Back to Events
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="bg-white shadow sm:rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-primary-100 p-2 rounded-lg">
                <UserIcon className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Profile Settings
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Update your personal and consent information.
                </p>
              </div>
            </div>
            {user && (
              <div className="text-right">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 italic">Consent Status:</span>
                  {getOverallConsentStatus(user) === "active" ? (
                    <Badge variant="success" size="sm">Active</Badge>
                  ) : (
                    <Badge variant="warning" size="sm">Review Required</Badge>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-4 py-5 sm:p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 pb-8 border-b border-gray-200">
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700"
                >
                  First name
                </label>
                <div className="mt-1">
                  <input
                    {...register("firstName")}
                    type="text"
                    id="firstName"
                    className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.firstName ? "border-red-300" : "border-gray-300"
                    }`}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.firstName.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Last name
                </label>
                <div className="mt-1">
                  <input
                    {...register("lastName")}
                    type="text"
                    id="lastName"
                    className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.lastName ? "border-red-300" : "border-gray-300"
                    }`}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="sm:col-span-2">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    {...register("email")}
                    type="email"
                    id="email"
                    className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.email ? "border-red-300" : "border-gray-300"
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h4 className="text-xl font-bold text-gray-900">
                Consents & Medical Information
              </h4>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>How consent works:</strong> Your consents are valid for 6 months from the date you last updated them. When you save your profile, any consent checkboxes you've changed will have their timestamps updated to today's date, extending their validity for another 6 months.
                </p>
                <p className="text-sm text-gray-700">
                  You can update your consents at any time from this profile page. This information will be used to automatically fill your booking forms, making the checkout process faster and easier.
                </p>
              </div>

              <ConsentFormFields
                register={register}
                errors={errors}
                watch={watch}
                name={user ? `${user.firstName} ${user.lastName}` : ""}
                user={user}
              />
            </div>

            <div className="flex justify-end pt-6 border-t border-gray-200 sticky bottom-0 bg-white/95 backdrop-blur py-4 -mx-6 px-6 shadow-[0_-4px_6px_-1px_rgb(0,0,0,0.1)]">
              <button
                type="submit"
                disabled={isUpdating}
                className="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Save className="w-5 h-5 mr-2" />
                )}
                Save And Update Consent
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="mt-12 space-y-8">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-bold text-gray-900">User Management</h3>
          {!isAddingUser && (
            <button
              onClick={() => setIsAddingUser(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </button>
          )}
        </div>

        {isAddingUser && (
          <div className="bg-white shadow sm:rounded-lg overflow-hidden border-2 border-primary-500">
            <div className="px-4 py-5 sm:px-6 bg-primary-50 border-b border-primary-100 flex justify-between items-center">
              <h4 className="text-lg font-medium text-primary-900">Add New User</h4>
              <button onClick={() => setIsAddingUser(false)} className="text-primary-500 hover:text-primary-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <form onSubmit={handleSubmitUser(onAddUser)} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">First Name</label>
                    <input
                      {...registerUser("firstName")}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                    {userErrors.firstName && <p className="mt-1 text-sm text-red-600">{userErrors.firstName.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Name</label>
                    <input
                      {...registerUser("lastName")}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                    {userErrors.lastName && <p className="mt-1 text-sm text-red-600">{userErrors.lastName.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      {...registerUser("email")}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                    {userErrors.email && <p className="mt-1 text-sm text-red-600">{userErrors.email.message}</p>}
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsAddingUser(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isUserSubmitting}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    {isUserSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Add User
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <UserList
          groupedUsers={groupedUsers}
          primaryGroupId={primaryGroupId}
          editingUserId={editingUserId}
          onEditUser={setEditingUserId}
          onCancelEdit={() => setEditingUserId(null)}
          onSaveUser={onEditUser}
        />
      </div>
    </div>
  );
}


